import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import DropdownController from "../../../components/inputs/DropdownController";
import TextFieldController from "../../../components/inputs/TextFieldController";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../../utils/functions";
import CityDialog from "../../../components/CityDialog";

// Validation schema
const emergencyContactSchema = yup.object().shape({
  name: yup
    .string()
    .max(100, maxLengthMessage(100))
    .required("Name is required"),
  phone: yup.string().max(50, maxLengthMessage(50)).nullable(),
  cellPhone: yup.string().max(50, maxLengthMessage(50)).nullable(),
  relationship: yup.string().nullable(),
  address: yup.string().max(250, maxLengthMessage(250)).nullable(),
  city: yup.string().max(50, maxLengthMessage(50)).nullable(),
  state: yup.string().nullable(),
  zip: yup.string().max(15, maxLengthMessage(15)).nullable(),
});

const EmergencyContactDialog = ({
  open,
  onClose,
  onSave,
  initialData,
  relationshipOptions,
  stateOptions,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(emergencyContactSchema),
    defaultValues: {
      name: initialData?.name || "",
      phone: initialData?.phone || "",
      cellPhone: initialData?.cellPhone || "",
      relationship: initialData?.relationship || "",
      address: initialData?.address || "",
      city: initialData?.city || "",
      state: initialData?.state || "",
      zip: initialData?.zip || "",
      orderOfContact: initialData?.orderOfContact || 0,
    },
  });
  const [openCityDialog, setOpenCityDialog] = useState(false);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const cityOptions = useMemo(() => {
    return (dictionary.codeCities || []).map((g) => ({
      label: g.State ? `${g.CityDescription}, ${g.State}` : g.CityDescription,
      value: g.CityDescription,
      state: g.State,
    }));
  }, [dictionary.codeCities]);

  const selectedCity = watch("city");

  useEffect(() => {
    if (selectedCity) {
      const matchedCity = cityOptions.find(
        (city) => city.value === selectedCity
      );
      if (matchedCity?.state) {
        setValue("state", matchedCity.state);
      }
    }
  }, [selectedCity, cityOptions, setValue]);

  useEffect(() => {
    reset({
      name: initialData?.name || "",
      phone: initialData?.phone || "",
      cellPhone: initialData?.cellPhone || "",
      relationship: initialData?.relationship || "",
      address: initialData?.address || "",
      city: initialData?.city || "",
      state: initialData?.state || "",
      zip: initialData?.zip || "",
      orderOfContact: initialData?.orderOfContact || 0,
    });
  }, [initialData, reset]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = (data) => {
    const contactData = {
      name: data.name?.trim(),
      phone: data.phone?.trim() || null,
      cellPhone: data.cellPhone?.trim() || null,
      relationship: data.relationship || null,
      address: data.address?.trim() || null,
      city: data.city?.trim() || null,
      state: data.state || null,
      zip: data.zip?.trim() || null,
      orderOfContact: data.orderOfContact || 0,
    };
    onSave(contactData);
    reset();
    onClose();
  };

  const handleIconClick = () => {
    setOpenCityDialog(true);
  };
  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {initialData ? "Edit Emergency Contact" : "Add Emergency Contact"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ mt: 1 }}
          >
            <Box sx={{ mb: 2 }}>
              <TextFieldController
                name="name"
                label="Name"
                type="text"
                control={control}
                errors={errors}
                required
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextFieldController
                name="phone"
                label="Phone"
                type="text"
                control={control}
                errors={errors}
                isPhoneNumber={true}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextFieldController
                name="cellPhone"
                label="Cell Phone"
                type="text"
                control={control}
                errors={errors}
                isPhoneNumber={true}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <DropdownController
                name="relationship"
                label="Relationship"
                options={relationshipOptions}
                control={control}
                errors={errors}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextFieldController
                name="address"
                label="Address"
                type="text"
                control={control}
                errors={errors}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <DropdownController
                name="city"
                label="City"
                options={cityOptions}
                control={control}
                errors={errors}
                startIcon={true}
                handleIconClick={handleIconClick}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <DropdownController
                name="state"
                label="State"
                options={stateOptions}
                control={control}
                errors={errors}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextFieldController
                name="zip"
                label="Zip"
                type="text"
                control={control}
                errors={errors}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <PATSaveButton onClick={handleSubmit(onSubmit)} tooltipText="Save" />
          <PATCloseButton onClick={onClose} tooltipText="Close" />
        </DialogActions>
      </Dialog>
      <CityDialog
        open={openCityDialog}
        onClose={() => {
          setOpenCityDialog(false);
        }}
      />
    </>
  );
};

export default EmergencyContactDialog;
